import React, { Suspense, useState, useEffect, useRef } from 'react';
import {
  Button,
  ConfirmationDialog,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@roger/ui-components';
import {
  FileText,
  Plus,
  ExternalLink,
  Pencil,
  Stamp,
  Trash2,
  Check,
  X,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useDocumentUrlRefresh } from '../hooks/useDocumentUrlRefresh';
import { useNeedsScrollablePdf } from '../hooks/useNeedsScrollablePdf';
import {
  OVERFLOW_TRIGGER_ATTR,
  useTabStripCapacity,
} from '../hooks/useTabStripCapacity';
import {
  UPLOAD_ACCEPT_ATTR,
  inferMimeType,
  viewerModeForDocument,
  isAllowedUploadFile,
  partitionDocTabs,
  docStatusDotClass,
  docStatusLabel,
} from './pdfViewerDocumentHelpers';

const ScrollablePdf = React.lazy(() => import('./ScrollablePdf'));

interface Document {
  id: string;
  fileName?: string;
  customName?: string;
  tooltipText?: string; // Optional custom tooltip text
  isPersisted?: boolean; // true = persisted to DB, false = local only (not yet uploaded)
  usedInExtraction?: boolean; // from API: true = used in AI extraction, false = added later
  canDelete?: boolean; // explicit delete permission, independent of usedInExtraction
  orderTypeTag?: string | null; // Optional order type tag for intake documents
  mimeType?: string | null;
  stampedAt?: string | null; // ISO timestamp of when this document was stamped, or null
}

interface PDFViewerNativeProps {
  documents: Document[];
  documentUrls: Record<string, string>;
  activeDocumentId: string;
  onActiveDocumentChange: (id: string) => void;
  onDocumentRename?: (documentId: string, fileName: string) => void;
  onDocumentAdd?: (file: File, displayName: string) => Promise<Document | null>;
  onDocumentDelete?: (documentId: string) => Promise<void>;
  onDocumentStamp?: (documentId: string) => Promise<void>;
  onRefreshUrl?: (documentId: string) => Promise<string | null>;
  onOrderTypeChange?: (documentId: string, orderTypeTag: string) => void;
  orderTypeOptions?: readonly { value: string; label: string }[];
  /** No add or rename; omit the delete handler too (External EMR chart lock). */
  readOnly?: boolean;
}

const PDFViewerNative: React.FC<PDFViewerNativeProps> = ({
  documents: initialDocuments,
  documentUrls: initialDocumentUrls,
  activeDocumentId,
  onActiveDocumentChange,
  onDocumentRename,
  onDocumentAdd,
  onDocumentDelete,
  onDocumentStamp,
  onRefreshUrl,
  onOrderTypeChange,
  orderTypeOptions,
  readOnly = false,
}) => {
  const [localDocuments, setLocalDocuments] = useState<Document[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editExtension, setEditExtension] = useState<string>('');
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);
  const [stampConfirmDocId, setStampConfirmDocId] = useState<string | null>(
    null
  );
  const [isStamping, setIsStamping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set());
  // A successful callback can rerender its parent before refreshed documents
  // reach props. Keep that brief stale render from restoring the deleted tab.
  const deletedDocumentIdsRef = useRef<Set<string>>(new Set());
  const localDocsRef = useRef<Document[]>([]);
  const activeDocumentIdRef = useRef(activeDocumentId);

  // URL refresh hook with debouncing and cancellation
  const {
    documentUrls: localDocumentUrls,
    isRefreshing: isRefreshingUrl,
    handleDebouncedRefresh,
    getUrlForNewTab,
    updateUrls,
    removeUrl,
  } = useDocumentUrlRefresh({
    onRefreshUrl,
    initialUrls: initialDocumentUrls,
  });

  // Keep ref in sync with localDocuments state (avoids stale closure in sync effect)
  useEffect(() => {
    localDocsRef.current = localDocuments;
  }, [localDocuments]);

  // Mirror the live active id so async cleanup paths reconcile against the
  // current value rather than the one captured in their closure. handleFileUpload
  // focuses its optimistic temp doc before awaiting; on a failed upload the catch
  // must compare against the committed active id (the temp doc), not the stale
  // pre-upload id the closure still holds.
  useEffect(() => {
    activeDocumentIdRef.current = activeDocumentId;
  }, [activeDocumentId]);

  // Initialize and sync local documents state with props
  useEffect(() => {
    const currentLocalDocs = localDocsRef.current;
    const incomingDocumentIds = new Set(initialDocuments.map(doc => doc.id));
    deletedDocumentIdsRef.current.forEach(documentId => {
      if (!incomingDocumentIds.has(documentId)) {
        deletedDocumentIdsRef.current.delete(documentId);
      }
    });

    const propDocs = initialDocuments
      .filter(doc => !deletedDocumentIdsRef.current.has(doc.id))
      .map(doc => {
        const existing = currentLocalDocs.find(d => d.id === doc.id);
        return {
          ...doc,
          isPersisted: true, // Documents from props are persisted
          usedInExtraction: doc.usedInExtraction ?? true, // Preserve from API
          // Only preserve existing customName if user has renamed it, don't default to fileName
          customName: existing?.customName,
          // Preserve optimistic orderTypeTag changes so re-fetches don't reset user edits
          orderTypeTag:
            existing !== undefined ? existing.orderTypeTag : doc.orderTypeTag,
          mimeType:
            doc.mimeType !== undefined &&
            doc.mimeType !== null &&
            doc.mimeType !== ''
              ? doc.mimeType
              : existing?.mimeType,
        };
      });

    // Create set of prop doc names to detect replaced temp docs
    const propDocNames = new Set(
      propDocs.map(d => d.customName || d.fileName).filter(Boolean)
    );

    // Only keep local-only docs that haven't been replaced by a saved doc
    // This fixes the race condition where temp docs would persist after upload
    const existingLocalOnlyDocs = currentLocalDocs.filter(d => {
      if (d.isPersisted) return false;
      // If there's now a saved doc with the same name, drop this temp doc
      const docName = d.customName || d.fileName;
      return !(docName && propDocNames.has(docName));
    });

    if (currentLocalDocs.length === 0) {
      setLocalDocuments(propDocs);
    } else {
      const mergedDocs = [...propDocs, ...existingLocalOnlyDocs];

      const currentKey = currentLocalDocs
        .map(d => `${d.id}:${d.orderTypeTag ?? ''}:${d.mimeType ?? ''}`)
        .join(',');
      const newKey = mergedDocs
        .map(d => `${d.id}:${d.orderTypeTag ?? ''}:${d.mimeType ?? ''}`)
        .join(',');
      if (currentKey !== newKey) {
        setLocalDocuments(mergedDocs);
      }
    }
  }, [initialDocuments]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Release the rename state if the document being renamed disappears — a temp
  // upload swapped for its saved row, or a delete. The editor unmounts without
  // firing blur, so neither saveEdit nor cancelEdit runs and `editingId` would
  // otherwise stay set to a dead id for the rest of the session. That now freezes
  // tab measurement (see `freezeMeasurement`), which would strand the strip at a
  // stale capacity until remount.
  useEffect(() => {
    if (editingId && !localDocuments.some(doc => doc.id === editingId)) {
      setEditingId(null);
    }
  }, [localDocuments, editingId]);

  // Helper function to extract extension from filename
  const getExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return ''; // No extension or filename starts with dot
    }
    return filename.substring(lastDotIndex);
  };

  // Helper function to get base name without extension
  const getBaseName = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return filename; // No extension or filename starts with dot
    }
    return filename.substring(0, lastDotIndex);
  };

  const startEditing = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    const fullName = doc.customName || doc.fileName || '';
    const extension = getExtension(fullName);
    const baseName = getBaseName(fullName);

    setEditingId(doc.id);
    setEditValue(baseName);
    setEditExtension(extension);
  };

  const saveEdit = () => {
    if (editingId && editValue.trim()) {
      const doc = localDocuments.find(d => d.id === editingId);
      // Append extension back to the edited name
      const newName = editValue.trim() + editExtension;

      setLocalDocuments(prev =>
        prev.map(d => (d.id === editingId ? { ...d, customName: newName } : d))
      );

      // Persist to backend if document is from the database
      if (doc?.isPersisted && onDocumentRename) {
        onDocumentRename(editingId, newName);
      }
    }
    setEditingId(null);
    setEditValue('');
    setEditExtension('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditExtension('');
  };

  // Handle document change - clear stale URL and fetch fresh one from server
  const handleDocumentChange = (documentId: string) => {
    // Immediately update the active document for responsive UI
    onActiveDocumentChange(documentId);

    // Clear the existing (potentially stale) URL so we don't show it
    // This will show a loading state until fresh URL arrives from server
    const doc = localDocuments.find(d => d.id === documentId);
    if (doc?.isPersisted) {
      removeUrl(documentId);
    }

    // Fetch fresh URL from server (debounced to handle rapid switching)
    handleDebouncedRefresh(documentId, { isPersisted: doc?.isPersisted });
  };

  // Handle opening document in new tab with fresh URL
  const handleOpenInNewTab = async () => {
    if (!activeDocumentId) return;
    const doc = localDocuments.find(d => d.id === activeDocumentId);
    const freshUrl = await getUrlForNewTab(activeDocumentId, {
      isPersisted: doc?.isPersisted,
    });
    if (freshUrl) {
      window.open(freshUrl, '_blank');
    }
  };

  const handleConfirmDelete = async () => {
    const documentId = deletingDocId;
    if (!documentId || !onDocumentDelete || isDeletingRef.current) return;

    const documentsBeforeDelete = localDocsRef.current;
    const deletedIndex = documentsBeforeDelete.findIndex(
      doc => doc.id === documentId
    );

    isDeletingRef.current = true;
    setIsDeleting(true);
    try {
      await onDocumentDelete(documentId);

      const remainingDocuments = localDocsRef.current.filter(
        doc => doc.id !== documentId
      );
      deletedDocumentIdsRef.current.add(documentId);
      localDocsRef.current = remainingDocuments;
      setLocalDocuments(remainingDocuments);
      removeUrl(documentId);

      if (activeDocumentIdRef.current === documentId) {
        const nextIndex = Math.min(
          Math.max(deletedIndex, 0),
          remainingDocuments.length - 1
        );
        onActiveDocumentChange(remainingDocuments[nextIndex]?.id ?? '');
      }
      setDeletingDocId(null);
    } catch {
      // The consumer shows the user-facing error. Keep the document and dialog
      // intact so the user can retry or cancel.
    } finally {
      isDeletingRef.current = false;
      setIsDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedUploadFile(file)) {
      alert(
        'Unsupported file type. Please upload a PDF, PNG, JPG, JPEG, DOC, or DOCX file.'
      );
      return;
    }

    const displayName = file.name.trim() || 'Untitled';

    // Create a temporary ID for immediate UI feedback
    const tempId = uuidv4();
    const newUrl = URL.createObjectURL(file);
    blobUrlsRef.current.add(newUrl);

    const tempDoc: Document = {
      id: tempId,
      fileName: displayName,
      customName: displayName,
      isPersisted: false,
      mimeType: inferMimeType(displayName, file.type || null),
    };

    setLocalDocuments(prev => [...prev, tempDoc]);
    updateUrls({ [tempId]: newUrl });
    onActiveDocumentChange(tempId);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onDocumentAdd) {
      try {
        const savedDoc = await onDocumentAdd(file, displayName);
        if (savedDoc) {
          const resolvedMime =
            savedDoc.mimeType ??
            inferMimeType(savedDoc.fileName ?? displayName, undefined);
          setLocalDocuments(prev =>
            prev.map(d =>
              d.id === tempId
                ? {
                    ...savedDoc,
                    customName: undefined,
                    fileName: savedDoc.fileName ?? displayName,
                    mimeType: resolvedMime ?? null,
                    isPersisted: true,
                  }
                : d
            )
          );
          updateUrls({ [savedDoc.id]: newUrl });
          removeUrl(tempId);
          onActiveDocumentChange(savedDoc.id);
        }
      } catch (err) {
        // A rejected onDocumentAdd means the upload failed (e.g. a background
        // uploader that reports failure by throwing). Drop the optimistic
        // "Uploading…" preview so it doesn't linger next to the error toast.
        // Consumers that report failure by returning null (not throwing) keep
        // their existing behavior — their temp doc is reconciled elsewhere.
        console.error('Error uploading document:', err);
        setLocalDocuments(prev => prev.filter(d => d.id !== tempId));
        removeUrl(tempId);
        URL.revokeObjectURL(newUrl);
        blobUrlsRef.current.delete(newUrl);
        if (activeDocumentIdRef.current === tempId) {
          onActiveDocumentChange('');
        }
      }
    }
  };

  const activeDoc =
    localDocuments.find(d => d.id === activeDocumentId) || localDocuments[0];
  const activeUrl = activeDoc ? localDocumentUrls[activeDoc.id] : null;
  const viewerMode = viewerModeForDocument(activeDoc);
  const needsScrollablePdf = useNeedsScrollablePdf();

  const { scrollContainerRef, tabsRowRef, capacity } = useTabStripCapacity({
    itemCount: localDocuments.length,
    // The rename editor swaps in a ~200px box; freeze measurement so it never
    // pollutes the capacity math or bumps the tab being renamed out mid-edit.
    freezeMeasurement: editingId !== null,
    // The active tab renders a hair wider (border + shadow); re-measure when it
    // changes so pinning an overflow doc can't leave a borderline clip.
    activeId: activeDocumentId,
  });

  // The active tab and any tab mid-rename must stay in the strip: the active
  // doc so its hover actions stay reachable, the editing doc so an in-progress
  // rename isn't discarded by an unmount when the pane narrows.
  const { visible: visibleDocs, overflow: overflowDocs } = partitionDocTabs(
    localDocuments,
    capacity,
    [activeDocumentId, editingId]
  );

  const renderDocTab = (doc: Document) => {
    const displayName = doc.customName || doc.fileName || 'Untitled';
    const statusLabel = docStatusLabel(doc);
    const isActive = doc.id === activeDocumentId;
    const isEditing = editingId === doc.id;

    if (isEditing) {
      return (
        <div
          key={doc.id}
          className='flex items-center gap-1 h-8 px-2 bg-background border border-border rounded flex-shrink-0'
          onClick={e => e.stopPropagation()}
        >
          <div className='flex items-center gap-0.5'>
            <Input
              ref={editInputRef}
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={saveEdit}
              maxLength={255 - editExtension.length}
              className='h-6 w-28 text-xs px-1.5'
            />
            {editExtension && (
              <span className='text-xs text-muted-foreground'>
                {editExtension}
              </span>
            )}
          </div>
          <button onClick={saveEdit} className='p-0.5 hover:bg-muted rounded'>
            <Check className='h-3 w-3 text-emerald-600' />
          </button>
          <button onClick={cancelEdit} className='p-0.5 hover:bg-muted rounded'>
            <X className='h-3 w-3 text-muted-foreground' />
          </button>
        </div>
      );
    }

    const tabButton = (
      <button
        onClick={() => handleDocumentChange(doc.id)}
        className={`
          group flex items-center gap-1.5 h-8 px-2.5 rounded text-xs font-medium transition-all flex-shrink-0
          ${
            isActive
              ? 'bg-background shadow-sm border border-border text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }
        `}
      >
        <div
          className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${docStatusDotClass(doc)}`}
        />
        <span className='truncate max-w-[80px]'>{displayName}</span>
        <span
          role='button'
          tabIndex={0}
          hidden={readOnly}
          onClick={e => startEditing(doc, e)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              startEditing(doc, e as unknown as React.MouseEvent);
            }
          }}
          className='p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity cursor-pointer'
          title='Rename'
        >
          <Pencil className='h-3 w-3 text-muted-foreground' />
        </span>
        {onDocumentStamp && doc.isPersisted && !doc.stampedAt && (
          <span
            role='button'
            tabIndex={0}
            onClick={e => {
              e.stopPropagation();
              setStampConfirmDocId(doc.id);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setStampConfirmDocId(doc.id);
              }
            }}
            className='p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity cursor-pointer'
            title='Append stamp page'
          >
            {isStamping && stampConfirmDocId === doc.id ? (
              <Loader2 className='h-3 w-3 text-muted-foreground animate-spin' />
            ) : (
              <Stamp className='h-3 w-3 text-muted-foreground' />
            )}
          </span>
        )}
        {(doc.usedInExtraction === false || doc.canDelete) &&
          onDocumentDelete && (
            <span
              role='button'
              tabIndex={0}
              onClick={e => {
                e.stopPropagation();
                setDeletingDocId(doc.id);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeletingDocId(doc.id);
                }
              }}
              aria-label={`Delete ${displayName}`}
              className='p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity cursor-pointer'
              title='Delete'
            >
              <Trash2 className='h-3 w-3 text-muted-foreground' />
            </span>
          )}
      </button>
    );

    if (onOrderTypeChange && orderTypeOptions) {
      return (
        <Popover key={doc.id}>
          <PopoverTrigger asChild>{tabButton}</PopoverTrigger>
          <PopoverContent
            side='bottom'
            className='w-56 p-3'
            onClick={e => e.stopPropagation()}
            onInteractOutside={e => {
              // Prevent popover from closing when interacting with Select portal
              const target = e.target as HTMLElement;
              if (
                target.closest(
                  '[role="listbox"], [role="option"], [data-radix-select-viewport]'
                )
              ) {
                e.preventDefault();
              }
            }}
          >
            <p className='text-xs font-medium mb-0.5 truncate'>
              {doc.tooltipText || displayName}
            </p>
            {statusLabel && (
              <p className='text-[10px] text-amber-500 mb-2'>{statusLabel}</p>
            )}
            <label className='text-[10px] text-muted-foreground'>
              Order type
            </label>
            <Select
              value={doc.orderTypeTag || 'none'}
              onValueChange={val => {
                const newTag = val === 'none' ? '' : val;
                // Optimistic local update
                setLocalDocuments(prev =>
                  prev.map(d =>
                    d.id === doc.id ? { ...d, orderTypeTag: newTag || null } : d
                  )
                );
                onOrderTypeChange(doc.id, newTag);
              }}
            >
              <SelectTrigger selectSize='sm' className='mt-1'>
                <SelectValue placeholder='Select order type' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='none'>No order type</SelectItem>
                {orderTypeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PopoverContent>
        </Popover>
      );
    }

    return (
      <Tooltip key={doc.id}>
        <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
        <TooltipContent side='bottom' className='text-xs'>
          {doc.tooltipText || displayName}
          {statusLabel && (
            <span className='text-amber-500 ml-1'>({statusLabel})</span>
          )}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className='h-full flex-1 overflow-hidden flex flex-col bg-background'>
        {/* Single Row: Tabs + Actions */}
        <div className='border-b bg-muted/20 flex items-center h-10'>
          {/* Scrollable Tab Container */}
          <div
            ref={scrollContainerRef}
            data-testid='pdf-viewer-tab-strip'
            className='flex-1 overflow-x-auto min-w-0'
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            <div
              ref={tabsRowRef}
              className='flex items-center gap-1 flex-nowrap hide-scrollbar'
            >
              {visibleDocs.map(renderDocTab)}
              {overflowDocs.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      // Width bookkeeping treats this as reclaimable space, not
                      // as a tab: it shrinks or disappears when tabs come back.
                      {...{ [OVERFLOW_TRIGGER_ATTR]: '' }}
                      className='flex items-center gap-1 h-8 px-2.5 rounded text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex-shrink-0 whitespace-nowrap'
                      title='More documents'
                    >
                      +{overflowDocs.length} more
                      <ChevronDown className='h-3 w-3' />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align='end'
                    className='max-h-80 overflow-y-auto'
                  >
                    {overflowDocs.map(doc => {
                      // Same precedence as the inline tab tooltip so same-named
                      // docs from different tasks (tooltipText carries the task
                      // prefix) stay distinguishable before selection.
                      const overflowName =
                        doc.tooltipText ||
                        doc.customName ||
                        doc.fileName ||
                        'Untitled';
                      const overflowStatus = docStatusLabel(doc);
                      return (
                        <DropdownMenuItem
                          key={doc.id}
                          onClick={() => handleDocumentChange(doc.id)}
                          className='gap-2'
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${docStatusDotClass(doc)}`}
                          />
                          <span className='truncate max-w-[240px]'>
                            {overflowName}
                          </span>
                          {overflowStatus && (
                            <span className='text-[10px] text-amber-500 flex-shrink-0'>
                              {overflowStatus}
                            </span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Action Buttons - Always visible */}
          <div className='flex items-center gap-1 flex-shrink-0 pl-2 border-l border-border/50 ml-2'>
            <input
              type='file'
              ref={fileInputRef}
              className='hidden'
              accept={UPLOAD_ACCEPT_ATTR}
              onChange={handleFileUpload}
            />
            <Button
              variant='ghost'
              size='sm'
              className='h-7 text-xs gap-1 px-2'
              onClick={() => fileInputRef.current?.click()}
              hidden={readOnly}
            >
              <Plus className='h-3.5 w-3.5' />
              Add
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleOpenInNewTab}
                  disabled={!activeUrl || isRefreshingUrl}
                  className='h-7 w-7 p-0'
                >
                  <ExternalLink className='h-3.5 w-3.5 text-muted-foreground' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom' className='text-xs'>
                Open in new tab
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex-1 overflow-hidden relative bg-muted/10'>
          {activeUrl ? (
            viewerMode === 'image' ? (
              <div className='flex h-full w-full items-center justify-center overflow-auto bg-muted/10 p-4'>
                <img
                  key={activeDocumentId}
                  src={activeUrl}
                  alt={activeDoc?.customName || activeDoc?.fileName || ''}
                  className='max-h-full max-w-full object-contain'
                />
              </div>
            ) : viewerMode === 'office' ? (
              <div className='flex h-full w-full flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground'>
                <FileText className='h-10 w-10 opacity-40' />
                <p className='text-sm text-foreground'>
                  Preview isn&apos;t available for Word documents.
                </p>
                <p className='text-xs'>
                  Use <span className='font-medium'>Open in new tab</span> to
                  view or download.
                </p>
              </div>
            ) : needsScrollablePdf && viewerMode === 'pdf' ? (
              // Touch browsers render a PDF <iframe> as a single, non-scrollable
              // first page. Stack pages in a scrollable container on touch-primary
              // devices only so narrow desktop windows keep native iframe controls.
              <Suspense
                fallback={
                  <div className='flex h-full w-full items-center justify-center'>
                    <Loader2 className='h-6 w-6 animate-spin text-muted-foreground/60' />
                  </div>
                }
              >
                <ScrollablePdf key={activeDocumentId} url={activeUrl} />
              </Suspense>
            ) : (
              <iframe
                key={activeDocumentId}
                src={
                  viewerMode === 'pdf' ? `${activeUrl}#navpanes=0` : activeUrl
                }
                className='w-full h-full border-0'
                title={
                  activeDoc?.customName ||
                  activeDoc?.fileName ||
                  'Document preview'
                }
              />
            )
          ) : activeDocumentId ? (
            // Document selected but URL is loading
            <div className='flex-1 h-full flex flex-col items-center justify-center text-muted-foreground gap-3'>
              <Loader2 className='h-8 w-8 animate-spin opacity-50' />
              <p className='text-sm'>Loading document...</p>
            </div>
          ) : (
            // No document selected
            <div className='flex-1 h-full flex flex-col items-center justify-center text-muted-foreground gap-2'>
              <FileText className='h-8 w-8 opacity-20' />
              <p className='text-sm'>Select a document to view</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        open={deletingDocId !== null}
        title='Delete document'
        message='Are you sure you want to delete this document? This action cannot be undone.'
        confirmLabel='Delete document'
        cancelLabel='Cancel'
        loading={isDeleting}
        onAccept={handleConfirmDelete}
        onCancel={() => {
          if (!isDeletingRef.current) setDeletingDocId(null);
        }}
      />

      <ConfirmationDialog
        open={stampConfirmDocId !== null}
        title='Append stamp page'
        message='This will permanently add a "Received by [initials] on [date]" page to the end of this PDF. This action cannot be undone.'
        confirmLabel='Stamp'
        cancelLabel='Cancel'
        loading={isStamping}
        onAccept={async () => {
          if (stampConfirmDocId && onDocumentStamp) {
            setIsStamping(true);
            try {
              await onDocumentStamp(stampConfirmDocId);
            } finally {
              setIsStamping(false);
            }
          }
          setStampConfirmDocId(null);
        }}
        onCancel={() => setStampConfirmDocId(null)}
      />
    </TooltipProvider>
  );
};

export default PDFViewerNative;
